<script>
  import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Select } from '@client/components/select';
  import { Spinner } from '@client/components/spinner';

  import { TagsService } from '@client/services/tags';

  export let row = {};
  export let tagFilters = {};

  let editing = false;
  let newItem = '';
  let previous = [];
  let saving = false;

  const dispatch = createEventDispatcher();

  const onSave = async () => {
    saving = true;

    const items = tags.length ? tags.join(', ') : null;

    await TagsService.update({ msglink: row.msglink, tags: items });

    dispatch('tags', { tags: items });

    editing = false;
    newItem = '';
    saving = false;
  };

  const onEdit = () => {
    previous = [...tags];
    editing = true;
  };

  const onCancel = () => {
    editing = false;
    newItem = '';
    tags = [...previous];
  };

  const addItem = (e) => {
    if (e.key === 'Enter') {
      if (!tags.includes(newItem) && newItem.length) {
        tags = [newItem, ...tags];
        newItem = '';
      }
    } else {
      if (!newItem.length && e.detail.value) {
        const selected = e.detail.value;

        if (!tags.includes(selected) && selected.length) {
          tags = [selected, ...tags];
        }

        tagFilters = { ...tagFilters };
      }
    }
  };

  const removeItem = (value) => {
    tags = tags.filter((tag) => tag !== value);
  };

  const formatItem = () => {
    newItem = newItem
      .trim()
      .replace(/\s/g, '-')
      .replace(/[^a-z0-9\-_]/gi, '')
      .toLowerCase();
  };

  let items = [];

  $: tags = row.tags?.split(', ') ?? [];
  $: items = Object.keys(tagFilters)
    .map((key) => !tags.includes(key) && tagFilters[key])
    .filter(Boolean);
  $: formatItem(newItem);
</script>

<div class="tags-container">
  <div class="tags-header">
    <h3>{$format('label.TAGS')}</h3>
    <div>
      {#if editing}
        <Button size="sm" color="secondary" on:click={onSave}>{$format('label.SAVE')}</Button>
        <Button size="sm" color="link" on:click={onCancel}>{$format('label.CANCEL')}</Button>
      {:else}
        <Button size="sm" color="secondary" on:click={onEdit}>{$format('label.MANAGE')}</Button>
      {/if}
    </div>
  </div>
  <div class="tags">
    <Spinner show={saving} backdrop />

    {#if editing}
      <div class="add" role="button" tabindex="0" on:keyup={addItem}>
        <Select
          {items}
          on:change={addItem}
          bind:filterText={newItem}
          placeholder={$format('label.PLACEHOLDER.SELECT_OR_CREATE_NEW_TAG')}
          searchable
        >
          <slot name="empty" slot="empty">
            <div class="empty">
              {@html $format('label.PRESS_ENTER_TO_CREATE_TAG', { values: { item: newItem } })}
            </div>
          </slot>
        </Select>
      </div>
    {/if}

    {#if tags.length}
      {#each tags as tag}
        <div class="tag">
          <Icon name="tag" />
          {tag}
          {#if editing}
            <Icon name="x" class="pointer" style="margin-right: -2px" role="button" tabindex="0" on:click={() => removeItem(tag)} />
          {/if}
        </div>
      {/each}
    {:else}
      {$format('label.NO_TAGS_ASSIGNED')}
    {/if}
  </div>
</div>

<style lang="scss">
  .tags-container {
    margin-top: var(--margin-large);

    .tags-header {
      align-items: center;
      display: flex;

      h3 {
        flex: 1;
      }
    }
  }

  .add {
    align-items: center;
    border-radius: var(--border-radius-base);
    margin: -4px 0 var(--margin-base) 0;
    width: 100%;
  }

  .empty {
    color: var(--color-gray-600);
    padding: var(--padding-xlarge);
    text-align: center;
  }

  .tags {
    background-color: var(--color-white);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-large);
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: var(--padding-large);
    position: relative;
    margin-top: var(--margin-large);

    .tag {
      align-items: center;
      background-color: var(--color-white);
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius-base);
      display: flex;
      gap: 4px;
      padding: var(--padding-small) var(--padding-base);


    }
  }
</style>
